// 问答系统配置文件
export interface QuizConfiguration {
  // 基础设置
  questionsPerRound: number;        // 每轮问答题目数量
  timePerQuestion: number;          // 每题答题时间（秒）
  
  // 显示设置
  showExplanation: boolean;         // 是否显示答案解释
  showCorrectAnswer: boolean;       // 错误时是否显示正确答案
  showSelectedAnswer: boolean;      // 错误时是否显示用户选择的答案
  
  // 流程控制
  autoNext: boolean;                // 是否自动进入下一题
  autoNextDelay: number;            // 自动进入下一题延迟（毫秒）
  
  // 触发条件
  triggerInterval: number;          // 触发间隔（完成几个省份触发一次）
  enabledModes: ('free' | 'timed' | 'quiz')[];  // 启用问答的游戏模式
  
  // 题目选择策略
  avoidRepeatProvinces: boolean;    // 避免重复选择同一省份
  preferRecentProvinces: boolean;   // 优先选择最近完成的省份
  
  // 分数设置
  correctScore: number;             // 答对得分
  timeBonus: boolean;               // 是否有时间奖励
  timeBonusMultiplier: number;      // 时间奖励倍数
}

// 预设配置
export const QUIZ_PRESETS = {
  // 简单模式 - 适合初学者
  easy: {
    questionsPerRound: 1,
    timePerQuestion: 45,
    showExplanation: true,
    showCorrectAnswer: true,
    showSelectedAnswer: true,
    autoNext: true,
    autoNextDelay: 3000,
    triggerInterval: 5,
    enabledModes: ['quiz'],
    avoidRepeatProvinces: false,
    preferRecentProvinces: true,
    correctScore: 20,
    timeBonus: false,
    timeBonusMultiplier: 1.0
  } as QuizConfiguration,
  
  // 标准模式 - 平衡的学习体验
  standard: {
    questionsPerRound: 3,
    timePerQuestion: 30,
    showExplanation: true,
    showCorrectAnswer: true,
    showSelectedAnswer: true,
    autoNext: true,
    autoNextDelay: 2000,
    triggerInterval: 3,
    enabledModes: ['quiz'],
    avoidRepeatProvinces: true,
    preferRecentProvinces: true,
    correctScore: 20,
    timeBonus: true,
    timeBonusMultiplier: 1.2
  } as QuizConfiguration,
  
  // 挑战模式 - 适合高级用户
  challenge: {
    questionsPerRound: 5,
    timePerQuestion: 20,
    showExplanation: true,
    showCorrectAnswer: true,
    showSelectedAnswer: false,
    autoNext: true,
    autoNextDelay: 1500,
    triggerInterval: 2,
    enabledModes: ['quiz', 'timed'],
    avoidRepeatProvinces: true,
    preferRecentProvinces: false,
    correctScore: 30,
    timeBonus: true,
    timeBonusMultiplier: 1.5
  } as QuizConfiguration,
  
  // 自定义模式 - 可完全自定义
  custom: {
    questionsPerRound: 3,
    timePerQuestion: 30,
    showExplanation: true,
    showCorrectAnswer: true,
    showSelectedAnswer: true,
    autoNext: true,
    autoNextDelay: 2000,
    triggerInterval: 3,
    enabledModes: ['quiz'],
    avoidRepeatProvinces: true,
    preferRecentProvinces: true,
    correctScore: 20,
    timeBonus: true,
    timeBonusMultiplier: 1.2
  } as QuizConfiguration
};

// 当前使用的配置（可以通过设置界面修改）
export let CURRENT_QUIZ_CONFIG: QuizConfiguration = QUIZ_PRESETS.standard;

// 更新配置的函数
export const updateQuizConfig = (newConfig: Partial<QuizConfiguration>) => {
  CURRENT_QUIZ_CONFIG = { ...CURRENT_QUIZ_CONFIG, ...newConfig };
  
  // 保存到本地存储
  localStorage.setItem('quizConfig', JSON.stringify(CURRENT_QUIZ_CONFIG));
  
  console.log('🔧 问答配置已更新:', CURRENT_QUIZ_CONFIG);
};

// 加载配置的函数
export const loadQuizConfig = () => {
  try {
    const saved = localStorage.getItem('quizConfig');
    if (saved) {
      CURRENT_QUIZ_CONFIG = { ...QUIZ_PRESETS.standard, ...JSON.parse(saved) };
      console.log('📥 已加载保存的问答配置');
    }
  } catch (error) {
    console.warn('⚠️ 加载问答配置失败，使用默认配置');
    CURRENT_QUIZ_CONFIG = QUIZ_PRESETS.standard;
  }
};

// 重置为预设配置
export const resetToPreset = (preset: keyof typeof QUIZ_PRESETS) => {
  CURRENT_QUIZ_CONFIG = { ...QUIZ_PRESETS[preset] };
  localStorage.setItem('quizConfig', JSON.stringify(CURRENT_QUIZ_CONFIG));
  console.log(`🔄 已重置为${preset}预设配置`);
};

// 获取当前配置
export const getCurrentQuizConfig = (): QuizConfiguration => {
  return CURRENT_QUIZ_CONFIG;
};

// 验证配置的函数
export const validateQuizConfig = (config: Partial<QuizConfiguration>): boolean => {
  try {
    // 基本范围检查
    if (config.questionsPerRound !== undefined && (config.questionsPerRound < 1 || config.questionsPerRound > 10)) {
      console.warn('questionsPerRound 应该在 1-10 之间');
      return false;
    }
    
    if (config.timePerQuestion !== undefined && (config.timePerQuestion < 10 || config.timePerQuestion > 120)) {
      console.warn('timePerQuestion 应该在 10-120 秒之间');
      return false;
    }
    
    if (config.autoNextDelay !== undefined && (config.autoNextDelay < 500 || config.autoNextDelay > 10000)) {
      console.warn('autoNextDelay 应该在 500-10000 毫秒之间');
      return false;
    }
    
    if (config.triggerInterval !== undefined && (config.triggerInterval < 1 || config.triggerInterval > 20)) {
      console.warn('triggerInterval 应该在 1-20 之间');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('配置验证失败:', error);
    return false;
  }
};

// 初始化配置
loadQuizConfig();
